import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Loader } from 'ncoded-component-library';
import api from '../../../../../../../../../api';
import { Listing } from '../../../../../../../../../models/Listing';
import EnquiryListingContext from './EnquiryListing.context';
import { ENQUIRY_ROUTES, EnquiryRoutes } from '../../utils';
import { formatAskingPrice, sentryError } from 'utils';
import { useTranslation } from 'react-i18next';
import { ASKING_PRICE_SELECT } from 'models/DealRoom';

type EnquiryListingProviderProps = {
  listingId: string;
  setClosed: () => void;
  setEnquiryRoute: Dispatch<SetStateAction<EnquiryRoutes>>;
};

const EnquiryListingProvider: React.FC<EnquiryListingProviderProps> = (
  props,
) => {
  const { children, listingId, setClosed, setEnquiryRoute } = props;
  const [listing, setListing] = useState<Listing>();

  const { t } = useTranslation();

  const priceGuide = useMemo(() => {
    if (!listing) return '';

    const { askingPriceSelect, amount, amountFrom, amountTo } = listing;
    if (askingPriceSelect === ASKING_PRICE_SELECT.EXPRESSION_OF_INTEREST)
      return t('eoi');
    if (askingPriceSelect === ASKING_PRICE_SELECT.PRICE_RANGE) {
      if (amountFrom && amountTo) {
        const formatAmountFrom = formatAskingPrice(amountFrom);
        const formatAmountTo = formatAskingPrice(amountTo);
        if (formatAmountFrom === formatAmountTo)
          return `${formatAmountFrom} ${t('priceGuide')}`;
        return `${formatAmountFrom} - ${formatAmountTo} ${t('priceGuide')}`;
      }
    } else if (amount) {
      return `${formatAskingPrice(amount)} ${t('priceGuide')}`;
    }

    return '';
  }, [listing, t]);

  useEffect(() => {
    listingId &&
      (async () => {
        setListing(null);
        try {
          const {
            data: { listing },
          } = await api.listings.getListing(listingId);

          setListing(listing);
          if (!listing) setEnquiryRoute(ENQUIRY_ROUTES.SIGN_UP);
        } catch (e) {
          sentryError(e);
        }
      })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId]);

  const providerValue = useMemo(
    () => ({
      listing,
      listingId,
      priceGuide,
      setClosed,
      setEnquiryRoute,
    }),
    [listing, listingId, priceGuide, setClosed, setEnquiryRoute],
  );

  return (
    <EnquiryListingContext.Provider value={providerValue}>
      {listing ? children : <Loader overlay={false} />}
    </EnquiryListingContext.Provider>
  );
};

export default EnquiryListingProvider;
